import { createContext } from 'react';
import type { SecurityOptIn } from '@shipium-corp/shipium-ts-types';
import type { User } from '~/store/services/AuthService/types';

export const PARTNER_VIEW_HEADER_KEY = 'x-partner-view';

/**
 * Whether the partner opt-ins are actually known.
 *
 * `isPartnerOptInsLoading` alone cannot answer that. TanStack Query v4 reports
 * `isLoading: true` both for a query that is genuinely in flight and for one that is
 * disabled and has never run, and reports `isLoading: false` for one that errored —
 * where the `= {}` default then makes an absent opt-in map look identical to a partner
 * that simply has no opt-ins. Callers that make access decisions need the difference:
 * `unavailable` must never be read as "this partner is not entitled".
 */
export type PartnerOptInsStatus = 'pending' | 'ready' | 'unavailable';

type SessionContextType = {
  user: User | undefined;
  sessionPartnerUrl: string;
  sessionPartnerId: string;
  partnerOptIns: Record<string, SecurityOptIn>;
  partnerOptInsStatus: PartnerOptInsStatus;
  /** Retries the opt-ins request, so an `unavailable` state is recoverable by the user. */
  refetchPartnerOptIns: () => void;
  isOptInEnabled: (optInId: string) => boolean;
  hasShipiumEmail: boolean;
  isShipiumView: boolean;
  setPartnerView: ({ type }: { type: 'set' | 'unset' }) => void;
  isPartnerOptInsLoading: boolean;
  returnToLogin: boolean;
};

export const SessionContext = createContext<SessionContextType>({
  user: undefined,
  sessionPartnerUrl: '',
  sessionPartnerId: '',
  partnerOptIns: {},
  partnerOptInsStatus: 'pending',
  refetchPartnerOptIns: () => void 0,
  isOptInEnabled: () => false,
  hasShipiumEmail: false,
  isShipiumView: false,
  setPartnerView: () => void 0,
  isPartnerOptInsLoading: true,
  returnToLogin: false,
});
